// fr.ts — French catalog for the owner dashboard. Translated per
// docs/superpowers/specs/locale-translator-contract.md (binding terminology, do-not-translate
// list, tone rules) and this catalog's own report at .superpowers/sdd/translator-fr-report.md.
// `satisfies Catalog` is the whole safety net: a missing key, a misspelled key, or a function
// with the wrong params is a `check-types` failure.
import { type Catalog } from './en';

export const fr = {
  common: {
    close: 'Fermer',
  },
  status: {
    pending: 'en attente',
    crawling: 'exploration',
    indexing: 'indexation',
    ready: 'prêt',
    error: 'erreur',
    paused: 'en pause',
    hint: 'astuce',
    deleting: 'suppression',
  },
  time: {
    never: 'jamais',
    justNow: "à l'instant",
    inAMoment: 'dans un instant',
    secondsAgo: ({ n }: { n: number }) => `il y a ${n} s`,
    inSeconds: ({ n }: { n: number }) => `dans ${n} s`,
    minutesAgo: ({ n }: { n: number }) => `il y a ${n} min`,
    inMinutes: ({ n }: { n: number }) => `dans ${n} min`,
    hoursAgo: ({ n }: { n: number }) => `il y a ${n} h`,
    inHours: ({ n }: { n: number }) => `dans ${n} h`,
    daysAgo: ({ n }: { n: number }) => `il y a ${n} j`,
    inDays: ({ n }: { n: number }) => `dans ${n} j`,
    monthsAgo: ({ n }: { n: number }) => `il y a ${n} mois`,
    inMonths: ({ n }: { n: number }) => `dans ${n} mois`,
    yearsAgo: ({ n }: { n: number }) => `il y a ${n} ans`,
    inYears: ({ n }: { n: number }) => `dans ${n} ans`,
  },
  shell: {
    impersonating: ({ email }: { email: string }) =>
      `Vous visualisez en tant que ${email} — vous usurpez l'identité de cet utilisateur.`,
    stopImpersonating: 'Arrêter',

    platformAdmin: 'Administration de la plateforme',
    adminTabWorkspaces: 'Workspaces',
    adminTabBots: 'Bots',
    adminTabJobs: 'Tâches',
    adminTabPosts: 'Articles',
    adminTabFeedback: 'Retours',
    adminTabPlayground: 'Playground',

    botsLoadError: "Impossible de charger vos bots.",
    botsLoadErrorInline: 'Impossible de charger vos bots',
    reloadToRetry: 'Rechargez la page pour réessayer.',

    navOverview: "Vue d'ensemble",
    navUnanswered: 'Sans réponse',
    navConversations: 'Conversations',
    navLeads: 'Leads',
    navSources: 'Sources',
    navPlayground: 'Playground',
    navAppearance: 'Apparence',
    navSettings: 'Paramètres',
    groupNeedsYou: 'À TRAITER',
    groupKnowledge: 'CONNAISSANCES',
    groupSetup: 'CONFIGURATION',

    closeMenu: 'Fermer le menu',
    navigationLabel: 'Navigation',
    openNavigation: 'Ouvrir la navigation',
    openAccountMenu: 'Ouvrir le menu du compte',

    switchBotAriaLabel: ({ name }: { name: string }) => `${name}, changer de bot`,
    botSwitcherHeader: ({ n, cap }: { n: number; cap: number | null }) =>
      `VOS BOTS${cap !== null ? ` · ${n} SUR ${cap} UTILISÉS` : ''}`,
    allBots: 'Tous les bots',
    newBot: 'Nouveau bot',
    newBotOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `Nouveau bot — ${n} sur ${cap} utilisés`,

    pagesIndexed: 'Pages indexées',

    feedbackTrigger: 'Retour',
    feedbackTypeGroupLabel: 'Type de retour',
    feedbackTypeBug: 'Bug',
    feedbackTypeFeature: 'Demande de fonctionnalité',
    feedbackTypeQuestion: 'Question',
    feedbackPlaceholder: "Que s'est-il passé, ou qu'est-ce qui améliorerait Embent pour vous ?",
    feedbackSent: 'Envoyé — nous lisons chaque message.',
    feedbackSendError: "Impossible d'envoyer le retour.",
    feedbackSending: 'Envoi…',
    feedbackSend: 'Envoyer',
    feedbackDialogTitle: 'Envoyer un retour',
    feedbackDialogDescription: "Bug, demande de fonctionnalité, ou question — ceci va directement à l'équipe.",

    workspaceSettings: 'Paramètres du workspace',
    members: 'Membres',
    billingUsage: 'Facturation & usage',
    switchWorkspace: 'Changer de workspace',
    accountLabelPrefix: 'Compte — ',
    account: 'Compte',
    signOut: 'Se déconnecter',
    themePrefix: 'Thème : ',
    languagePrefix: 'Langue : ',
    nextPrefix: 'suivant : ',
    themeSystem: 'Système',
    themeDark: 'Sombre',
    themeLight: 'Clair',
    roleOwner: 'Propriétaire',
    roleAdmin: 'Admin',
    roleMember: 'Membre',
    workspaceSubline: ({ plan, role }: { plan: string | null; role: string }) =>
      plan ? `${plan} · vous êtes ${role}` : `Vous êtes ${role}`,
    workspaceMenuAriaLabel: ({ name }: { name: string }) => `${name}, menu du workspace et du compte`,

    usageWarning: ({ pct }: { pct: number }) =>
      `Vous avez utilisé ${pct} % de vos messages mensuels — passez à un plan supérieur pour garder chaque conversation fluide.`,
    dismiss: 'Ignorer',

    renewsOn: ({ date }: { date: string }) => `Renouvellement le ${date}`,
    resetsOn: ({ date }: { date: string }) => `Réinitialisation le ${date}`,
    answersPaused: 'Nouvelles réponses en pause — au-delà de 110 % du plafond',
    messagesLeft: ({ n }: { n: string }) => `${n} messages restants ce mois-ci`,
    badgeStillShown: ({ plan }: { plan: string }) => `${plan} affiche encore le badge « Powered by Embent »`,
    badgeOptional: ({ plan }: { plan: string }) => `${plan} rend le badge « Powered by Embent » facultatif`,
    upgradeCta: ({ price }: { price: number }) => `Passer au plan supérieur — ${price} $/mois`,
    resumeAnswersCta: ({ price }: { price: number }) => `Reprendre les réponses — ${price} $/mois`,
    resumeAnswersBenefit: 'Passer au plan supérieur reprend les réponses immédiatement',
    upgradeBenefit: ({
      plan,
      messages,
      bots,
      seats,
    }: {
      plan: string;
      messages: string;
      bots: number;
      seats: number;
    }) => `${plan} ajoute ${messages} messages, ${bots} bots et ${seats} ${seats > 1 ? 'utilisateurs' : 'utilisateur'}`,
  },
  bots: {
    noSources: 'aucune source',
    allSourcesReady: ({ n }: { n: number }) => `${n} · toutes prêtes`,
    sourceStatusRollup: ({ n, status }: { n: number; status: string }) => `${n} · ${status}`,
    paused: 'En pause',
    sourcesRemoving: ({ n }: { n: number }) => `${n} source${n > 1 ? 's' : ''} · suppression`,
    sourcesSyncError: ({ n }: { n: number }) => `${n} source${n > 1 ? 's' : ''} · erreur de synchronisation`,
    sourcesSyncing: ({ n }: { n: number }) => `${n} source${n > 1 ? 's' : ''} · synchronisation`,
    messagesUnanswered: ({ messages, unanswered }: { messages: number; unanswered: number }) =>
      `${messages} message${messages > 1 ? 's' : ''} · ${unanswered} sans réponse`,
    botsCount: ({ n }: { n: number }) => `${n} bot${n > 1 ? 's' : ''}`,
    botsOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `${n} sur ${cap} utilisés`,

    colBot: 'Bot',
    colStatus: 'Statut',
    colMessages14d: 'Messages · 14j',
    colUnanswered: 'Sans réponse',
    colSources: 'Sources',
    pageTitle: 'Bots',
    emptyTitle: 'Créez votre premier bot',
    emptyDescription:
      "Pointez-le vers votre site web ou votre doc — il explore le contenu et commence à répondre aux questions des visiteurs.",
    planIncludesBots: ({ cap }: { cap: number }) => `Votre plan inclut ${cap} bot${cap > 1 ? 's' : ''}`,
    upgradeGateDescription: ({ name, priceUsd, maxBots }: { name: string; priceUsd: number; maxBots: number }) =>
      `${name} (${priceUsd} $/mois) permet jusqu'à ${maxBots} bots — un par site, chacun avec ses propres connaissances et son propre style.`,
    comparePlansLink: 'Comparer les plans →',
    newBotDialogTitle: 'Nouveau bot',
    botNameFieldLabel: 'Nom',
    botNamePlaceholder: 'Bot support Acme',
    creatingCta: 'Création…',
    createCta: 'Créer',
  },
  auth: {
    emailLabel: 'Email',
    passwordLabel: 'Mot de passe',
    genericFormError: 'Une erreur est survenue. Merci de réessayer.',
    continueWithGoogle: 'Continuer avec Google',
    backToSignIn: 'Retour à la connexion',

    signInTitle: 'Se connecter',
    signInSubtitle: 'Content de vous revoir sur Embent.',
    passwordJustResetNotice: 'Mot de passe mis à jour — connectez-vous avec votre nouveau mot de passe.',
    forgotPasswordLink: 'Mot de passe oublié ?',
    unverifiedResendNotice: "Vérifiez votre boîte de réception — nous avons renvoyé l'email de vérification.",
    signingIn: 'Connexion…',
    signInCta: 'Se connecter',
    noAccountPrompt: 'Pas encore de compte ? ',
    signUpLink: "S'inscrire",

    signUpTitle: 'Créez votre compte',
    signUpSubtitle: 'Commencez à répondre aux questions de vos visiteurs en quelques minutes.',
    nameLabel: 'Nom',
    creatingAccount: 'Création du compte…',
    createAccountCta: 'Créer le compte',
    haveAccountPrompt: 'Déjà un compte ? ',
    signInLink: 'Se connecter',

    checkEmailTitle: 'Vérifiez votre email',
    checkEmailSubtitle: "Nous vous avons envoyé un lien de vérification. Il arrive généralement en moins d'une minute.",
    checkEmailBody: "Cliquez sur le lien dans l'email pour activer votre compte.",

    verifyFailedTitle: 'Échec de la vérification',
    verifiedTitle: 'Email vérifié',
    verifyFailedBody: 'Ce lien de vérification est invalide ou a expiré.',
    verifiedBody: 'Tout est prêt — direction votre tableau de bord…',

    requestResetTitle: 'Réinitialisez votre mot de passe',
    requestResetSubtitlePending: 'Entrez votre email et nous vous enverrons un lien de réinitialisation.',
    requestResetSubtitleSent: 'Si cet email correspond à un compte Embent, nous avons envoyé un lien de réinitialisation.',
    requestResetBodySent: 'Vérifiez votre boîte de réception. Le lien expire dans 1 heure.',
    sendingResetLink: 'Envoi…',
    sendResetLinkCta: 'Envoyer le lien de réinitialisation',

    resetLinkInvalidTitle: 'Lien de réinitialisation invalide',
    resetLinkInvalidBody: "Ce lien de réinitialisation de mot de passe n'a pas de jeton, ou a déjà été utilisé.",
    requestNewLinkCta: 'Demander un nouveau lien',
    chooseNewPasswordTitle: 'Choisissez un nouveau mot de passe',
    chooseNewPasswordSubtitle: "Choisissez-en un que vous n'avez pas encore utilisé.",
    newPasswordLabel: 'Nouveau mot de passe',
    resetLinkExpiredFallback: 'Ce lien de réinitialisation est invalide ou a expiré.',
    savingPassword: 'Enregistrement…',
    saveNewPasswordCta: 'Enregistrer le nouveau mot de passe',

    inviteNotValidTitle: 'Invitation invalide',
    goToEmbentLink: 'Aller sur Embent',
    inviteInvalidFallback: "Cette invitation n'est plus valide.",
    inviteTitle: ({ workspaceName }: { workspaceName: string }) => `Vous êtes invité à rejoindre ${workspaceName}`,
    inviteDescription: ({ email }: { email: string }) =>
      `Envoyée à ${email} — connectez-vous ou créez un compte avec cet email pour accepter.`,
    inviteSignInCta: 'Se connecter',
    inviteSignUpCta: 'Créer un compte',
    wrongAccountTitle: 'Mauvais compte',
    signOutCta: 'Se déconnecter',
    inviteAcceptFallback: "Une erreur est survenue lors de l'acceptation de l'invitation.",
    joiningWorkspaceTitle: ({ workspaceName }: { workspaceName: string }) => `Adhésion à ${workspaceName}…`,
    joiningWorkspaceBody: 'Un instant.',

    eventLabels: {
      newLead: 'nouveau lead',
      unansweredDigest: 'résumé des questions sans réponse',
      crawlFailed: 'échec du crawl',
      weeklySummary: 'résumé hebdomadaire',
    },
    unsubscribeLinkExpiredTitle: 'Ce lien a expiré',
    unsubscribeLinkExpiredBody: "Gérez les notifications depuis les paramètres du bot dans le tableau de bord.",
    openDashboardCta: 'Ouvrir le tableau de bord',
    unsubscribedTitle: 'Vous êtes désinscrit',
    unsubscribedBody: ({ eventLabel, botName }: { eventLabel: string; botName: string | null }) =>
      `Vous ne recevrez plus d'emails de ${eventLabel} pour ${botName ?? 'ce bot'}. Vous pouvez les réactiver dans les paramètres du bot.`,

    notFoundEyebrow: 'Page introuvable',
    notFoundTitle: "Rien n'existe à cette adresse",
    notFoundBody: 'Le lien est peut-être obsolète, ou la page a été déplacée.',
    notFoundCta: 'Aller à vos bots',

    landingBotsErrorFallback: 'Une erreur est survenue lors du chargement de vos bots.',
    landingGoToBotsCta: 'Aller à vos bots →',
  },
  onboarding: {
    skipForNow: "Passer pour l'instant",
    invalidWebsiteUrl: 'Entrez une URL de site web valide, par ex. https://acme.vn',
    websiteUrlLabel: 'URL du site web',
    genericFallbackError: 'Une erreur est survenue.',

    crawlDefaultHostname: 'votre site',
    noSourceToReplace: 'Aucune source à remplacer pour le moment.',

    stepWebsite: 'Site web',
    stepCrawl: 'Exploration',
    stepTryIt: 'Essai',
    stepLook: 'Apparence',
    stepGoLive: 'Mise en ligne',
    stepMobileReadout: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Étape ${current} sur ${total} · ${label}`,
    stepProgressAriaLabel: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Progression de l'intégration : étape ${current} sur ${total}, ${label}`,
    stepTablistAriaLabel: "Progression de l'intégration",

    websiteHeading: 'Ajoutez votre site web',
    websiteSubtitle:
      "Embent lit votre site et construit un bot qui répond avec des citations — rien d'inventé. La plupart des sites sont prêts en quelques minutes.",
    botNameLabel: 'Nom du bot',
    botNamePlaceholder: 'Acme Support',
    websiteUrlPlaceholder: 'acme.vn',
    websiteUrlHelper: "Nous trouverons vos pages automatiquement. Vous pourrez ajouter d'autres sources plus tard.",
    botNameRequired: 'Entrez un nom pour votre bot.',
    websiteStarting: 'Démarrage…',
    websiteSubmitCta: 'Commencer à lire mon site',

    crawlHeading: ({ hostname }: { hostname: string }) => `Lecture de ${hostname}…`,
    crawlSubtitleReady: 'Terminé — chaque page est indexée et prête à répondre.',
    crawlSubtitleSyncing: "Vous n'avez pas besoin d'attendre ici — essayez votre bot pendant qu'il termine.",
    crawlStatusLines: {
      pending: ["En file d'attente — démarrage imminent…", 'Préparation du démarrage…'],
      crawling: ['Recherche des pages du site…', 'Suivi des liens…', 'Lecture des pages…'],
      indexing: ["Construction de l'index de recherche…", 'Traitement du contenu lu…'],
    },
    crawlPagesIndexed: ({ n }: { n: number }) => `${n} pages indexées`,
    crawlNoSourceYet: "Nous n'avons pas encore trouvé de source pour ce bot.",
    crawlErrorLead:
      "La cause la plus probable est une erreur de frappe dans l'adresse — vérifiez-la ci-dessous et réessayez.",
    crawlReplaceUrlCta: 'Essayer une autre adresse',
    crawlReplaceUrlUpdating: 'Mise à jour…',
    crawlReplaceUrlHelper: 'Nous supprimerons la tentative échouée et repartirons de zéro avec cette adresse.',
    crawlRetryCta: 'Réessayer',
    crawlRetrying: 'Nouvelle tentative…',
    crawlRetryHelper:
      "Si l'adresse est correcte et que le site était simplement injoignable un instant, réessayez plutôt. Les sites " +
      "qui ont besoin de JavaScript pour afficher leur contenu peuvent être gérés avec un réglage disponible après " +
      "la configuration, sur l'écran Sources.",
    crawlCtaSyncing: "Essayer votre bot pendant qu'il termine →",
    crawlCtaReady: 'Suivant : essayez votre bot →',

    tryItHeading: "Posez-lui n'importe quelle question",
    tryItSubtitleWithPages: ({ n }: { n: number }) =>
      `Il connaît déjà ${n} page${n > 1 ? 's' : ''} — et continue d'apprendre le reste. Chaque réponse montre ses sources.`,
    tryItSubtitleNoPages:
      "Il est encore en train de lire votre site — posez quand même une question ; les réponses ne feront que s'améliorer.",
    tryItSamplePrompt: `Essayez : « Quelle est votre politique de retour ? »`,
    tryItPlaceholder: 'Posez une question sur la livraison, les tarifs, tout…',
    tryItSending: 'Envoi…',
    tryItSendCta: 'Envoyer',
    tryItNextCta: 'Ça me va — personnalisons-le',

    lookHeading: 'Adaptez-le à votre site',
    lookSubtitle:
      "Choisissez votre couleur de marque — l'aperçu se met à jour au fur et à mesure. Vous pourrez tout ajuster plus tard dans Apparence.",
    brandColorLabel: 'Couleur de marque',
    colorHexAriaLabel: 'Valeur hexadécimale de la couleur',
    welcomeMessageLabel: "Message d'accueil",
    lookSaving: 'Enregistrement…',
    lookContinueCta: 'Continuer',

    goLiveHeading: "Un seul extrait et vous êtes en ligne",
    goLivePasteBeforePrefix: 'Collez ceci avant la balise',
    goLivePasteBeforeSuffix: "fermante sur votre site. C'est tout — le bot apparaît en bas à droite.",
    goLiveInstallGuidesLink: "Guides d'installation par plateforme",
    goLiveFirstAnswerNotice: "Nous vous enverrons un email dès que le widget répondra à son premier visiteur.",
    goLiveDashboardCta: 'Aller au tableau de bord',
    goLiveViewSourcesCta: 'Voir les sources',
  },
  overview: {
    title: "Vue d'ensemble",
    liveOnPrefix: 'En ligne sur ',
    answeringSince: ({ date }: { date: string }) => `répond depuis le ${date}`,
    testInPlaygroundCta: 'Tester dans le Playground',
    addSourcesCta: 'Ajouter des sources',
    botDetailsLoadError: ({ message }: { message: string }) => `Impossible de charger les détails du bot — ${message}`,

    messagesLabel: 'Messages · 14 derniers jours',
    conversationsLink: 'Conversations →',
    vsPreviousPeriod: 'vs les 14 jours précédents',
    leadsLabel: 'Leads capturés',
    newLeadsCount: ({ n }: { n: number }) => `${n} nouveaux`,
    leadsThisWeek: ({ n }: { n: number }) => `${n} cette semaine`,
    viewLeadsLink: 'Voir les leads →',
    unansweredLabel: 'Questions sans réponse',
    fixThemLink: 'Les corriger →',

    knowledgeHeading: 'Connaissances',
    pointAtSite: 'Pointez-le vers votre site',
    addSourceLink: 'Ajouter une source →',
    addSourcesLink: 'Ajouter des sources →',
    pagesIndexed: 'Pages indexées',
    sourcesCountLabel: 'Sources',
    lastSyncedLabel: 'Dernière synchronisation',
    manageSourcesLink: 'Gérer les sources →',
    resyncing: 'Resynchronisation…',
    allSyncing: 'Synchronisation en cours',
    resyncCta: 'Resynchroniser',
    resyncNothingToSync: 'Rien à synchroniser — chaque source est déjà en cours de synchronisation.',
    resyncGenericError: 'Impossible de démarrer une resynchronisation — merci de réessayer.',

    latestConversationsHeading: 'Dernières conversations',
    allConversationsLink: 'Toutes les conversations →',
    noConversationsEmpty: 'Calme ces 14 derniers jours — les nouvelles conversations apparaîtront ici.',
    noMessageFallback: '(aucun message)',
    leadCapturedTag: 'Lead capturé',
    hasUnansweredTag: 'A une question sans réponse',

    knowledgeAllReady: 'Tout est prêt',
    knowledgeSyncing: 'Sources en synchronisation',
    knowledgeNeedsAttention: "Une source a besoin d'attention",
    knowledgeRemoving: 'Suppression de sources',

    createdSubtitle: ({ time }: { time: string }) => `Créé ${time}`,
    zeroStatMessagesHint: 'Commence à la première question',
    zeroStatLeadsHint: 'Nom et email, en cours de conversation',
    zeroStatUnansweredHint: 'Rien à corriger pour le moment',
    heroReadyHeadline: 'Votre bot est prêt à répondre',
    heroIndexingHeadline: 'Encore en train de lire votre site',
    heroEmptyHeadline: 'Ajoutez une source pour commencer',
    heroEmptyBody:
      "Il n'a encore rien à lire. Ajoutez une source et il sera prêt à répondre en quelques minutes.",
    heroIndexingErrorBody:
      "Une source a besoin d'attention — elle ne se terminera pas toute seule. Vérifiez la carte Connaissances pour voir ce qui ne va pas, puis mettez-le sur votre site une fois corrigé.",
    heroIndexingPrefix: 'Il est encore en train de lire',
    heroIndexingNoSourceFallback: 'votre source',
    heroIndexingSuffix: ' — revenez dans quelques minutes, puis mettez-le sur votre site.',
    heroReadyBodyPrefix: ({ n }: { n: number }) => `Il a lu ${n} page${n > 1 ? 's' : ''}`,
    heroReadyFromDomain: ' de ',
    heroReadyBodySuffix:
      " et les connaît assez bien pour répondre à leur sujet. Il ne reste qu'une étape : le mettre sur votre site.",
    onlyPagesNudge: ({ n }: { n: number }) =>
      `Seulement ${n} page${n > 1 ? 's' : ''} ? Ajoutez vos pages de FAQ, de tarifs ou de politiques pour lui donner plus de matière.`,
    addSourceCta: 'Ajouter une source',
    addSourceHelperText:
      "Cela prend environ une minute. L'extrait à intégrer sera là, prêt, dès qu'il aura de quoi répondre.",
    pasteBeforeBodyLabel: 'COLLEZ CECI AVANT </BODY>',
    emailSnippetLink: "Envoyer l'extrait par email à mon développeur",
    emailSnippetSubject: ({ botName }: { botName: string }) => `Intégrer ${botName} sur notre site`,
    emailSnippetBody: ({ snippet }: { snippet: string }) =>
      `Collez ceci avant la balise </body> fermante :\n\n${snippet}`,
    whatHappensNextHeading: 'Ce qui se passe ensuite',
    stepVisitorAsksTitle: 'Un visiteur demande quelque chose',
    stepVisitorAsksBody: "La bulle se trouve en bas à droite de chaque page où l'extrait est présent. Rien d'autre à configurer.",
    stepEmailAnswerTitle: 'Nous vous envoyons la première réponse par email',
    stepEmailAnswerBody: "Pour que vous lisiez exactement ce qu'il a dit, dans les mots de votre client, avant tout le monde.",
    stepUnansweredTitle: "Ce qu'il n'a pas pu répondre atterrit ici",
    stepUnansweredBody: "Il refuse plutôt que d'inventer. Chaque refus devient une ligne à corriger pour vous.",
    stepAddSourceTitle: 'Ajoutez une source',
    stepAddSourceBody:
      "Pointez-le vers votre site, ou téléversez des fichiers et du texte collé — il lui faut quelque chose à lire avant de pouvoir répondre.",
    stepAddSourceVisitorTitle: 'Un visiteur demande, vous recevez la réponse par email',
    countersHeading: 'LES COMPTEURS DÉMARRENT AVEC VOTRE PREMIER VISITEUR',

    needsYouHeading: 'À traiter',
    allUnansweredLink: 'Toutes les questions sans réponse →',
    needsYouEmpty: "Rien à corriger — votre bot a répondu à tout ce qu'on lui a demandé.",
    needsYouIntro: "Votre bot a dit qu'il ne savait pas. Répondez une fois et il le saura pour toujours.",
    askedSummary: ({ n, time }: { n: number; time: string }) =>
      `${n === 1 ? 'Demandé une fois' : `Demandé ${n} fois`} · dernière fois ${time}`,
    writeAnswerCta: 'Rédiger une réponse',
    cancelCta: 'Annuler',
    answerPlaceholder: "Rédigez la réponse que vous donneriez à ce visiteur. Le bot l'utilisera désormais.",
    addingCta: 'Ajout…',
    addAsQaSourceCta: 'Ajouter comme source Q&R',
    saveAnswerError: "Impossible d'enregistrer cette réponse — merci de réessayer.",
  },
  sources: {
    pageTitle: 'Sources',
    sourcesSubtitle: ({
      n,
      pageCount,
      lastSynced,
    }: {
      n: number;
      pageCount: number;
      lastSynced: string;
    }) =>
      `${n} source${n > 1 ? 's' : ''} · ${pageCount} page${pageCount > 1 ? 's' : ''} · dernière synchronisation ${lastSynced}`,
    botLoadError: ({ message }: { message: string }) => `Impossible de charger ce bot — ${message}`,

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Texte',
    typeFile: 'Fichier',

    colType: 'Type',
    colUri: 'URI',
    colStatus: 'Statut',
    colRenderedMode: 'Mode rendu',
    colPages: 'Pages',
    colLastSynced: 'Dernière synchronisation',
    colActions: 'Actions',

    urlFieldLabel: 'URL de la page',
    urlPlaceholder: 'https://example.com/docs',
    sitemapFieldLabel: 'URL du sitemap',
    sitemapPlaceholder: 'https://example.com/sitemap.xml',
    textTitleFieldLabel: 'Titre',
    textTitlePlaceholder: 'Politique de remboursement',
    textContentFieldLabel: 'Contenu',
    textContentPlaceholder: 'Collez le texte que le bot doit connaître…',
    fileFieldLabel: 'Fichier',
    fileDropPrompt: 'Déposez un fichier ici, ou cliquez pour parcourir',
    fileDropCaption: ({ extensions, maxSize }: { extensions: string; maxSize: string }) =>
      `${extensions} · jusqu'à ${maxSize}`,
    fileTooLarge: ({ maxSize }: { maxSize: string }) => `Ce fichier dépasse la limite de ${maxSize}.`,
    fileTypeNotSupported: ({ ext, extensions }: { ext: string; extensions: string }) =>
      `${ext ? `.${ext}` : 'Ce fichier'} n'est pas pris en charge — utilisez ${extensions}.`,
    filesDroppedNote: ({ name, count }: { name: string; count: number }) =>
      `Utilisation de « ${name} » — ${count} autre${count > 1 ? 's' : ''} fichier${count > 1 ? 's' : ''} déposé${count > 1 ? 's' : ''}, ignoré${count > 1 ? 's' : ''}.`,
    fileDropNotAFile: "Ce n'était pas un fichier — glissez plutôt un fichier depuis votre ordinateur.",
    fileTitleFieldLabel: 'Titre (facultatif)',
    fileTitlePlaceholder: 'Par défaut, le nom du fichier',

    addingCta: 'Ajout…',
    addSourceCta: 'Ajouter une source',

    emptyTitle: 'Pointez-le vers votre site',
    emptyDescription: "Ajoutez une URL, un sitemap, du texte ou un fichier ci-dessus et nous commencerons à le lire.",

    reviewPagesLink: 'Examiner les pages',
    nearEmptyInfoAriaLabel: ({ count }: { count: number }) => `${count} page${count > 1 ? 's' : ''} semblaient vides`,

    resyncCta: 'Resynchroniser',
    deleteCta: 'Supprimer',
    cancelCta: 'Annuler',
    syncInProgressError: 'Synchronisation en cours — réessayez plus tard',

    deleteConfirmTitle: 'Supprimer cette source ?',
    deleteConfirmDescriptionPrefix: 'Supprime ',
    deleteConfirmDescriptionSuffix: " et tout ce qu'elle a indexé. Cette action est irréversible.",

    renderedModeExplainerAriaLabel: "Qu'est-ce que le mode rendu ?",
    renderedModeExplainerBody1Bold: 'Le mode rendu',
    renderedModeExplainerBody1Rest:
      " exécute chaque page dans un vrai navigateur avant de la lire, pour que le contenu qui n'apparaît qu'après l'exécution de JavaScript soit capturé.",
    renderedModeExplainerBody2:
      "Laissez-le désactivé pour les sites normaux — c'est plus rapide et moins coûteux. Activez-le quand une page semble vide pour le crawler (applications monopages, tableaux de bord, tout ce qui charge son texte après l'ouverture de la page).",
    renderedModeExplainerBody3:
      'Compromis : le crawl est plus lent et utilise davantage de votre budget de pages par exécution.',

    renderedModeLabel: 'Mode rendu',
    modeRendered: 'Rendu',
    modeStandard: 'Standard',
    switchModeTitle: ({ mode }: { mode: string }) => `Passer en mode ${mode} ?`,
    switchModeDescription: ({ mode }: { mode: string }) =>
      `Ceci re-crawle la source en mode ${mode}. Les pages qui reviennent différentes sont réindexées et celles qui n'existent plus sont supprimées — l'index actuel reste disponible jusqu'à la fin du nouveau crawl.`,
    switchModeCta: ({ mode }: { mode: string }) => `Passer en mode ${mode}`,
  },
  sourceDetail: {
    crawledPages: ({ n }: { n: number }) => `${n} page${n > 1 ? 's' : ''} explorée${n > 1 ? 's' : ''}.`,
    crawledPagesWithSkipped: ({ n, failedCount }: { n: number; failedCount: number }) =>
      `${n} page${n > 1 ? 's' : ''} explorée${n > 1 ? 's' : ''}. ${failedCount} page${failedCount > 1 ? 's' : ''} n'${failedCount > 1 ? 'ont' : 'a'} pas pu se charger et ${failedCount > 1 ? 'ont' : 'a'} été ignorée${failedCount > 1 ? 's' : ''}.`,
    truncatedCapacity: ({ n }: { n: number }) =>
      `${n} page${n > 1 ? 's' : ''} explorée${n > 1 ? 's' : ''} avant que notre propre capacité de crawl ne soit épuisée. C'est temporaire de notre côté — attendez quelques minutes et relancez le crawl.`,
    truncatedDeadline: ({ n }: { n: number }) =>
      `${n} page${n > 1 ? 's' : ''} explorée${n > 1 ? 's' : ''} avant que ce crawl n'atteigne notre propre limite de temps.`,
    truncatedAttempts: ({ n }: { n: number }) =>
      `${n} page${n > 1 ? 's' : ''} explorée${n > 1 ? 's' : ''} avant que le crawl n'épuise ses tentatives sur des pages qui ne se chargeaient pas.`,
    truncatedPageCap: ({ n }: { n: number }) =>
      `${n} page${n > 1 ? 's' : ''} explorée${n > 1 ? 's' : ''} — le maximum que le plafond de pages de votre plan permet actuellement.`,
    remainingAtLeast: ({ n }: { n: number }) =>
      ` Au moins ${n} page${n > 1 ? 's' : ''} n'${n > 1 ? 'ont' : 'a'} pas été indexée${n > 1 ? 's' : ''}.`,
    remainingNone: " Certaines pages n'ont peut-être pas été indexées.",
    crawlFailedGeneric: "Ce crawl n'a indexé aucune page.",

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Texte',
    typeFile: 'Fichier',

    languagePrefix: 'Langue : ',

    factType: 'Type',
    factRenderedMode: 'Mode rendu',
    factLastSynced: 'Dernière synchronisation',

    resyncCta: 'Resynchroniser',
    deleteCta: 'Supprimer',

    latestCrawlHeading: 'Dernier crawl',
    neverCrawledYet: "Cette source n'a pas encore été explorée.",
    crawlRanInMode: ({ rendered }: { rendered: boolean }) =>
      `Ce crawl s'est exécuté en mode ${rendered ? 'rendu' : 'standard'}.`,

    failedUrlsHeading: 'URL échouées',
    failedUrlsCountCaveat: ({ total, shown }: { total: number; shown: number }) =>
      ` — ${total} au total, affichage des ${shown} premières`,
    offSiteRedirectsHeading: 'Redirections hors site',
    offSiteRedirectsMoreCaveat: ' — il peut y en avoir plus que ce que cette liste montre',
    nearEmptyCameBack: ({ n }: { n: number }) =>
      `${n} page${n > 1 ? 's' : ''} ${n > 1 ? 'sont revenues' : 'est revenue'} avec presque aucun contenu.`,

    pagesIndexedHeading: 'Pages indexées',
    nearEmptyOnlyToggle: 'Quasi-vides uniquement',

    noPagesIndexedYet: 'Aucune page indexée pour le moment.',
    colUrl: 'URL',
    colCharacters: 'Caractères',
    colStatus: 'Statut',
    nearEmptyBadge: 'quasi-vide',

    indexStateIndexed: 'Indexée',
    indexStateIndexing: 'Indexation…',
    indexStateFailed: 'Échec',

    fileNoMarkdownExplainer:
      "Stocké tel quel, en tant que fichier téléversé — nous conservons l'original, pas une copie texte séparée, donc il n'y a rien à prévisualiser ici. Le fichier est indexé et le bot répond à partir de lui.",
    nothingStoredForDocument: 'Rien de stocké pour ce document.',

    storedContentHeading: 'Contenu stocké',
    nothingStoredYet: 'Rien de stocké pour le moment.',

    deleteDialogTitle: 'Supprimer cette source ?',
    deleteDialogDescriptionPrefix: 'Supprime ',
    deleteDialogDescriptionSuffix: " et tout ce qu'elle a indexé. Cette action est irréversible.",
    cancelCta: 'Annuler',
  },
  conversations: {
    today: "Aujourd'hui",
    yesterday: 'Hier',
    yourTesting: 'Vos tests',
    visitor: 'Visiteur',
    playgroundTag: 'Playground',
    noMessageFallback: '(aucun message)',
    leadCapturedTag: 'Lead capturé',
    hasUnansweredTag: 'A une question sans réponse',
    unansweredChipLabel: 'Sans réponse',
    loadingMore: 'Chargement…',
    loadMore: 'Charger plus',
    selectConversationPrompt: 'Sélectionnez une conversation pour voir sa transcription.',
    backToConversations: '← Retour aux conversations',
    transcriptStartedSummary: ({ time, n }: { time: string; n: number }) => `Démarrée ${time} · ${n} messages`,
    deleteConversationAriaLabel: 'Supprimer cette conversation',
    leadSubmittedTag: '✓ Lead envoyé',
    cancelCta: 'Annuler',
    deletingCta: 'Suppression…',

    pageTitle: 'Conversations',
    headerSubtitle: ({ n }: { n: number }) => `${n} conversation${n > 1 ? 's' : ''} · les plus récentes en premier`,
    emptyTitle: 'En attente de votre premier visiteur',
    emptyDescription: "Les conversations apparaissent ici dès que quelqu'un discute avec votre bot.",

    deleteAllCta: 'Supprimer toutes les conversations',
    deleteDialogTitle: 'Supprimer cette conversation ?',
    deleteDialogDescription:
      "Supprime définitivement cette transcription et ses messages. Un lead capturé dedans reste dans l'onglet Leads. Cette action est irréversible.",
    deleteCta: 'Supprimer la conversation',

    deleteAllDialogTitle: 'Supprimer toutes les conversations ?',
    deleteAllDescriptionPrefix:
      "Supprime définitivement chaque conversation et message de ce bot. Les leads capturés restent dans l'onglet Leads. Cette action est irréversible. Tapez ",
    deleteAllDescriptionSuffix: ' pour confirmer.',
    confirmBotNameAriaLabel: 'Confirmer le nom du bot',
  },
  leads: {
    filterNew: 'Nouveaux',
    filterContacted: 'Contactés',
    filterArchived: 'Archivés',
    filterAll: 'Tous',
    actionMarkNew: 'Marquer comme nouveau',
    actionMarkContacted: 'Marquer comme contacté',
    actionArchive: 'Archiver',
    statusNew: 'nouveau',
    statusContacted: 'contacté',
    statusArchived: 'archivé',

    filterByStatusAriaLabel: 'Filtrer par statut',
    pageTitle: 'Leads',
    exportingCta: 'Export…',
    exportCsvCta: 'Exporter en CSV',
    exportCsvError: "Impossible d'exporter les leads — merci de réessayer.",

    headerSubtitleAll: ({ n }: { n: number }) =>
      `${n} lead${n > 1 ? 's' : ''} capturé${n > 1 ? 's' : ''} · les plus récents en premier`,
    headerSubtitleFiltered: ({ n, filterLabel }: { n: number; filterLabel: string }) =>
      `${n} lead${n > 1 ? 's' : ''} ${filterLabel} · les plus récents en premier`,

    deleteAllCta: 'Supprimer tous les leads',
    bulkActionsAriaLabel: 'Actions groupées',
    selectedCount: ({ n }: { n: number }) => `${n} sélectionné${n > 1 ? 's' : ''}`,
    bulkCapMessage: ({ max }: { max: number }) => `Sélectionnez jusqu'à ${max} leads à la fois`,
    bulkUpdatedMessage: ({ n }: { n: number }) => `${n} mis à jour${n > 1 ? 's' : ''}`,
    bulkUpdateError: 'Impossible de mettre à jour ces leads. Réessayez.',

    emptyTitle: 'En attente de votre premier lead',
    emptyDescription: 'Les leads capturés par le widget de chat de votre bot apparaîtront ici.',
    noFilterResultsTitle: ({ filterLabel }: { filterLabel: string }) => `Aucun lead ${filterLabel}`,
    noFilterResultsDescription: 'Rien dans ce filtre pour le moment.',

    selectAllAriaLabel: 'Sélectionner tous les leads',
    colName: 'Nom',
    colContact: 'Contact',
    colIntent: 'Intention',
    colStatus: 'Statut',
    colCaptured: 'Capturé',
    colActions: 'Actions',

    noEmailFallback: "(pas d'email)",
    selectLeadAriaLabel: ({ contact }: { contact: string }) => `Sélectionner le lead ${contact}`,
    deleteLeadAriaLabel: ({ contact }: { contact: string }) => `Supprimer le lead ${contact}`,
    playgroundTag: 'Playground',
    viewChatCta: 'Voir le chat',
    loadingMoreCta: 'Chargement…',
    loadMoreCta: 'Charger plus',

    cancelCta: 'Annuler',
    deletingCta: 'Suppression…',

    deleteDialogTitle: 'Supprimer ce lead ?',
    deleteDescriptionPrefix: 'Supprime définitivement ',
    deleteDescriptionSuffix: '. Cette action est irréversible.',
    noContactFallback: 'ce lead',
    deleteCta: 'Supprimer le lead',

    deleteAllDialogTitle: 'Supprimer tous les leads ?',
    deleteAllDescriptionPrefix:
      'Supprime définitivement chaque lead capturé pour ce bot. Cette action est irréversible. Tapez ',
    deleteAllDescriptionSuffix: ' pour confirmer.',
    confirmBotNameAriaLabel: 'Confirmer le nom du bot',
  },
  unanswered: {
    tabOpen: 'Ouvertes',
    tabDismissed: 'Ignorées',
    tabAnswered: 'Répondues',
    filterByStatusAriaLabel: 'Filtrer par statut',

    pageTitle: 'Sans réponse',
    headerSubtitle: ({ n, verb }: { n: number; verb: string }) => `${n} question${n > 1 ? 's' : ''} ${verb}`,
    subtitleVerbOpen: 'en attente de réponse',
    subtitleVerbDismissed: 'ignorée',
    subtitleVerbAnswered: 'répondue',

    openTabIntro:
      "Les questions auxquelles votre bot n'a pas pu répondre, regroupées. Rédigez une réponse une fois — elle devient une source Q&R et le bot la connaît pour toujours.",

    emptyTitleOpen: "Rien d'ouvert — votre bot a répondu à tout ce qu'on lui a demandé.",
    emptyTitleDismissed: "Rien d'ignoré.",
    emptyTitleAnswered: 'Aucune question répondue pour le moment.',

    askedOnce: 'Demandé une fois',
    askedTimes: ({ n }: { n: number }) => `Demandé ${n} fois`,

    viewSourceCta: 'Voir la source',
    sourceRemoved: 'source supprimée',
    viewChatCta: 'Voir le chat',
    chatDeleted: 'conversation supprimée',

    dismissCta: 'Ignorer',
    reopenCta: 'Rouvrir',
    writeAnswerCta: 'Rédiger une réponse',
    cancelCta: 'Annuler',
    answerPlaceholder: "Rédigez la réponse que vous donneriez à ce visiteur. Le bot l'utilisera désormais.",
    addingCta: 'Ajout…',
    addAsQaSourceCta: 'Ajouter comme source Q&R',
    answerAddedNotice: "Ajoutée comme source Q&R — le bot l'utilisera dès la prochaine conversation.",
    saveAnswerError: "Impossible d'enregistrer cette réponse — merci de réessayer.",
  },
  appearance: {
    pageTitle: 'Apparence',
    pageSubtitle: 'Comment le widget apparaît et accueille les visiteurs sur votre site',

    colorLabel: 'Couleur',
    colorHexAriaLabel: 'Valeur hexadécimale de la couleur',
    botNameLabel: 'Nom du bot',
    botNamePlaceholder: 'Acme Support',
    welcomeMessageLabel: "Message d'accueil",
    welcomePlaceholder: "Bonjour ! Comment puis-je vous aider aujourd'hui ?",

    quickQuestionsLabel: 'Questions rapides',
    addQuestionCta: 'Ajouter une question',
    quickQuestionsHint:
      "Facultatif — affichées comme des puces sur lesquelles les visiteurs peuvent taper avant même d'avoir écrit quoi que ce soit.",
    quickQuestionAriaLabel: ({ n }: { n: number }) => `Question rapide ${n}`,
    quickQuestionPlaceholder: 'Quels sont vos horaires ?',
    removeCta: 'Retirer',

    positionLabel: 'Position du lanceur',
    positionBottomRight: 'En bas à droite',
    positionBottomLeft: 'En bas à gauche',

    languageLabel: 'Langue',
    autoLanguageLabel: 'Selon la langue du navigateur du visiteur',

    badgeLabel: 'Badge « Powered by Embent »',
    badgeHint: 'Affiche un petit lien « Powered by Embent » dans le pied du widget.',
    badgeSwitchAriaLabel: 'Afficher le badge « Powered by Embent »',
    badgeLoadError: 'Impossible à charger — actualisez',
    freeBadgeNote: ({ plans }: { plans: string }) => `Passer à ${plans} supprime cette obligation.`,

    saveCta: 'Enregistrer',
    savingCta: 'Enregistrement…',
    savedLabel: 'Enregistré',

    livePreviewHeading: 'Aperçu en direct',
    previewCaption: "Seuls le bouton du lanceur et le panneau s'affichent ici — la page autour est vide, volontairement.",
    devHintPrefix: "Si rien n'apparaît, le bundle du widget n'est pas construit localement : lancez",
    devHintIn: 'dans',
    devHintAndCopy: 'et copiez',
    devHintTo: 'vers',

    widgetPreviewTitle: 'Aperçu du widget',
  },
  botSettings: {
    pageTitle: 'Paramètres',
    pageSubtitle: 'Domaines autorisés, capture de leads, et comment le bot se présente',

    leadCaptureOffLabel: 'Désactivée',
    leadCaptureOffBlurb: 'Le bot ne demande jamais de coordonnées aux visiteurs.',
    leadCaptureOnIntentLabel: 'Sur intention',
    leadCaptureOnIntentBlurb:
      "Le bot demande un email ou un numéro de téléphone seulement quand il ne peut pas répondre ou que le visiteur souhaite un suivi.",
    leadCaptureAlwaysLabel: 'Toujours',
    leadCaptureAlwaysBlurb:
      "Le bot demande des coordonnées une fois, juste après le premier échange d'une conversation.",
    leadCapturePreChatLabel: 'Formulaire avant chat',
    leadCapturePreChatBlurb:
      "Les visiteurs remplissent un court formulaire de contact (nom, email, téléphone facultatif) avant que le chat ne commence. Le bot ne redemande jamais en cours de conversation.",

    leadLevelSubtleLabel: 'Discret',
    leadLevelSubtleBlurb: 'Propose un suivi uniquement quand le visiteur demande à être contacté ou souhaite un devis.',
    leadLevelBalancedLabel: 'Équilibré (recommandé)',
    leadLevelBalancedBlurb:
      "Propose une fois quand le visiteur montre de vrais signaux d'achat — demande comment démarrer, souhaite une démo, ou mentionne son propre site.",
    leadLevelProactiveLabel: 'Proactif',
    leadLevelProactiveBlurb:
      "Propose chaque fois que le visiteur semble évaluer l'offre, et redemande une fois de plus plus tard si la première proposition est restée sans réponse.",

    domainRequiredError: 'Requis — retirez la ligne plutôt que de la laisser vide',
    domainBareHostnameError: "Nom d'hôte seul uniquement, par ex. « example.com » (sans schéma ni chemin)",
    domainTooLongError: 'Trop long',

    aboutLabel: 'À propos de ce bot',
    aboutHint:
      "Une courte présentation que le bot connaît toujours — qui il est, qui il sert — pour que des questions comme " +
      '« qui êtes-vous ? » obtiennent une réponse instantanée sans recherche.',
    aboutPlaceholder:
      "Nous sommes l'assistant support d'Acme, ici pour aider les clients à configurer et dépanner les Acme Widgets.",

    allowedDomainsLabel: 'Domaines autorisés',
    addDomainCta: 'Ajouter un domaine',
    allowedDomainsHint:
      "Sites autorisés à intégrer le widget de ce bot. Laissez vide pour autoriser tous les sites — il est recommandé " +
      'de restreindre cela à votre ou vos propres domaines avant la mise en ligne.',
    domainAriaLabel: ({ n }: { n: number }) => `Domaine autorisé ${n}`,
    domainPlaceholder: 'example.com',
    removeCta: 'Retirer',

    leadCaptureLabel: 'Capture de leads',
    leadLevelLabel: 'À quel point proactif ?',

    saveCta: 'Enregistrer',
    savingCta: 'Enregistrement…',
    savedLabel: 'Enregistré',

    embedSnippetHeading: 'Extrait à intégrer',
    embedPastePrefix: 'Collez ceci avant la balise',
    embedPasteSuffix: 'fermante sur votre site.',

    embedCopyCta: 'Copier',
    embedCopiedCard: 'Copié !',
    embedCopiedCheck: '✓ Copié',

    dangerZoneHeading: 'Zone de danger',
    dangerZoneDescription:
      'Supprime le bot, ses sources, ses conversations, ses leads, et son index de recherche. Cette action est irréversible.',
    deleteBotCta: 'Supprimer ce bot',
    deleteBotDialogTitle: ({ name }: { name: string }) => `Supprimer « ${name} » ?`,
    dangerZoneDialogDescriptionPrefix:
      'Supprime le bot, ses sources, ses conversations, ses leads, et son index de recherche. Cette action est irréversible. Tapez ',
    dangerZoneDialogDescriptionSuffix: ' pour confirmer.',
    confirmBotNameAriaLabel: 'Confirmer le nom du bot',
    cancelCta: 'Annuler',
    deletingCta: 'Suppression…',
  },
  notifications: {
    heading: 'Notifications',
    subheading: "Les emails vont à l'adresse du compte de chaque personne. L'activité du Playground ne déclenche jamais d'emails.",
    noMembers: 'Aucun membre.',
    saveError: "Impossible d'enregistrer ce réglage. Réessayez.",

    roleOwner: 'Propriétaire',
    roleAdmin: 'Admin',
    roleMember: 'Membre',
    cadenceOff: 'Désactivé',
    cadenceDaily: 'Quotidien',
    cadenceWeekly: 'Hebdomadaire',

    colMember: 'Membre',
    colNewLead: 'Nouveau lead',
    colUnansweredDigest: 'Résumé des questions sans réponse',
    colCrawlFailed: 'Échec du crawl',
    colWeeklySummary: 'Résumé hebdomadaire',

    newLeadAriaLabel: ({ email }: { email: string }) => `Emails de nouveau lead pour ${email}`,
    unansweredDigestAriaLabel: ({ email }: { email: string }) => `Emails de résumé des questions sans réponse pour ${email}`,
    crawlFailedAriaLabel: ({ email }: { email: string }) => `Emails d'échec de crawl pour ${email}`,
    weeklySummaryAriaLabel: ({ email }: { email: string }) => `Emails de résumé hebdomadaire pour ${email}`,
  },
  playground: {
    pageTitle: 'Playground',
    pageSubtitle: "Discutez avec votre bot comme le ferait un visiteur — rien ici n'atteint votre site.",
    retrievalDebugLabel: 'Débogage de la recherche',
    hideDebugCta: 'Masquer le débogage',
    newSessionCta: 'Nouvelle session',
    emptyStateText: "Envoyez un message pour tester comment ce bot répond, en s'appuyant sur ses sources.",
    inputPlaceholder: 'Posez une question à ce bot…',
    stopCta: 'Arrêter',
    sendCta: 'Envoyer',
    debugEmptyText: "Posez une question pour voir ce qu'il récupère",
    genericError: 'Une erreur est survenue.',
  },
  billing: {
    pageTitle: 'Facturation',
    openingCta: 'Ouverture…',
    manageBillingCta: 'Gérer la facturation',

    memberGateTitle: 'La facturation est gérée par le propriétaire ou un admin de votre workspace.',
    memberGateDescription: "Demandez à un propriétaire ou un admin d'apporter des changements au plan de ce workspace.",

    nowOnPlan: ({ name }: { name: string }) => `Vous êtes maintenant sur le plan ${name}.`,
    finishingUp: "Nous finalisons cela — actualisez dans un instant si votre plan ne se met pas à jour.",
    confirmingWithPolar: 'Changement reçu — confirmation avec Polar…',

    internalWorkspaceLabel: 'Workspace interne',
    monthlyPriceLabel: ({ price }: { price: number }) => `${price} $ / mois`,
    internalWorkspaceInline: "Aucune limite, la facturation ne s'applique pas.",
    internalWorkspaceHeading: "Workspace interne — aucune limite, la facturation ne s'applique pas.",
    internalWorkspaceBody:
      "Chaque plafond de plan est contourné et il n'y a rien à mettre à niveau ou à payer ici. L'usage est " +
      "toujours enregistré ci-dessus pour information, mais il n'est jamais transmis à Polar — ce workspace " +
      "n'a pas d'identité de facturation.",

    unlimitedWord: 'illimité',
    unlimitedParens: '(illimité)',
    ofCap: ({ cap }: { cap: string }) => `/ ${cap}`,
    internalRenewalFallback: "Workspace interne · aucune limite · la facturation ne s'applique pas",
    capsSuffix: ({
      bots,
      botsCount,
      messages,
      pages,
    }: {
      bots: string;
      botsCount: number | null;
      messages: string;
      pages: string;
    }) => `${bots} bot${(botsCount ?? 0) > 1 ? 's' : ''} · ${messages} messages · ${pages} pages`,
    freeForever: ({ capsSuffix }: { capsSuffix: string }) => `Gratuit à vie · ${capsSuffix}`,
    renewsOn: ({ date, capsSuffix }: { date: string; capsSuffix: string }) =>
      `Renouvellement le ${date} · ${capsSuffix}`,

    cancelsAtPeriodEnd: 'Annulation à la fin de la période',
    planChangesPausedNotice: "Les changements de plan sont suspendus jusqu'à la fin de la période en cours.",
    planChangesPausedReason:
      "Cet abonnement est programmé pour être annulé à la fin de la période — reprenez-le d'abord pour changer de plans.",

    redirectingCta: 'Redirection…',
    switchingCta: 'Changement…',
    upgradeToCta: ({ name }: { name: string }) => `Passer à ${name}`,

    messagesThisMonthLabel: 'Messages ce mois-ci',
    pagesIndexedLabel: 'Pages indexées',
    messageNoun: 'message',
    pageNoun: 'page',
    messagesUnit: ({ n }: { n: string }) => `${n} messages`,
    usageLimitWarning: ({ pct, noun }: { pct: number; noun: string }) => `${pct} % de votre limite de ${noun}`,
    upgradeRaisesTo: ({ name, unit }: { name: string; unit: string }) => ` — ${name} la porte à ${unit}.`,
    limitReached: ' atteinte.',

    cancelSubscriptionCta: "Annuler l'abonnement",
    cancelingCta: 'Annulation…',
    cancelDialogTitle: "Annuler l'abonnement ?",
    yourCurrentPlanFallback: 'votre plan actuel',
    cancelDialogDescription: ({
      planName,
      renewsDate,
    }: {
      planName: string;
      renewsDate: string | null;
    }) =>
      `Vos bots continuent de fonctionner sur ${planName} jusqu'à la fin de cette période de facturation${
        renewsDate ? ` (${renewsDate})` : ''
      } — puis le workspace repasse au plan Free. Cela n'arrive jamais immédiatement.`,
    keepSubscriptionCta: "Garder l'abonnement",

    changePlanFallbackTitle: 'Changer de plan ?',
    keepCurrentPlanCta: 'Garder le plan actuel',
    confirmFallbackCta: 'Confirmer',

    checkoutErrorFallback: 'Une erreur est survenue au démarrage du paiement.',
    portalErrorFallback: "Une erreur est survenue à l'ouverture de la gestion de la facturation.",
    cancelErrorFallback: "Une erreur est survenue lors de l'annulation de votre abonnement.",

    currentPlanBadge: 'Plan actuel',
    botsUnit: ({ n }: { n: number }) => `${n} bot${n > 1 ? 's' : ''}`,
    seatsUnit: ({ n }: { n: number }) => `${n} ${n > 1 ? 'utilisateurs' : 'utilisateur'}`,
    messagesPerMonth: ({ n }: { n: string }) => `${n} messages / mois`,
    pagesUnit: ({ n }: { n: string }) => `${n} pages`,
    badgeRequiredText: 'Badge « Powered by Embent »',
    badgeOptionalText: 'Badge « Powered by Embent » facultatif',
    upgradeCta: 'Passer au plan supérieur',
    switchToCta: ({ name }: { name: string }) => `Passer à ${name}`,

    flatPricingNote:
      'Tarif fixe, aucun calcul par utilisateur, aucun dépassement surprise — nous vous prévenons à 80 % et ' +
      "suspendons les nouvelles réponses seulement au-delà de 110 %, et n'interrompons jamais une conversation en cours.",

    planChangeConfirm: {
      verbUpgrade: 'Mettre à niveau',
      verbSwitch: 'Changer',
      title: ({ verb, name }: { verb: string; name: string }) => `${verb} vers ${name} ?`,
      confirmNow: ({ verb }: { verb: string }) => `${verb} maintenant`,
      continueToPolarCta: 'Continuer vers Polar',
      checkoutDescription: ({ name, price }: { name: string; price: number }) =>
        `Vous allez être redirigé vers Polar pour démarrer un abonnement ${name} (${price} $/mois) — Polar vous ` +
        'facture là-bas pour l\'activer.',
      switchDescription: ({ name, price }: { name: string; price: number }) =>
        `Ceci change votre plan immédiatement — vos bots utilisent les limites de ${name} ` +
        `(${price} $/mois) dès maintenant. Polar ne vous facture rien aujourd'hui : il applique la ` +
        'différence proratisée à votre prochaine facture.',
    },
  },
  workspace: {
    pageTitle: 'Paramètres du workspace',
    loadErrorMessage: "Impossible de charger ce workspace. Rechargez la page pour réessayer.",
    notAllowedTitle: 'Seuls les propriétaires et les admins peuvent modifier les paramètres du workspace.',
    notAllowedDescription: "Demandez à un propriétaire ou un admin de ce workspace d'apporter des changements.",

    nameLabel: 'Nom du workspace',
    nameHint: ({ min, max }: { min: number; max: number }) =>
      `${min} à ${max} caractères. Visible par tout le monde dans ce workspace et dans les emails d'invitation.`,

    uploadLogoCta: 'Téléverser un logo',
    removeLogoCta: 'Retirer',
    logoHint: "Un carré fonctionne mieux. PNG, JPEG, WebP ou GIF, jusqu'à 2 Mo.",
    logoNotConfiguredError: "Le téléversement de logo n'est pas configuré sur cet environnement.",
    uploadLogoErrorFallback: 'Impossible de téléverser le logo. Réessayez.',
    removeLogoErrorFallback: 'Impossible de retirer le logo. Réessayez.',

    saveNameErrorFallback: "Impossible d'enregistrer le nom. Réessayez.",
    saveCta: 'Enregistrer',
    savedLabel: 'Enregistré',

    cropDialogTitle: 'Recadrez votre logo',
    cropDialogDescription: 'Glissez pour repositionner, zoomez pour ajuster. Enregistré sous forme de carré 256×256.',
    zoomLabel: 'Zoom',
    cropErrorFallback: 'Impossible de recadrer cette image. Essayez un autre fichier.',
    cancelCta: 'Annuler',
    useThisLogoCta: 'Utiliser ce logo',
  },
  members: {
    pageTitle: 'Membres',
    membersOnlyTitle: 'Zone réservée aux membres',
    membersOnlyDescription: "Demandez à votre propriétaire de workspace ou à un admin de gérer les membres.",
    unlimitedCapWord: 'Illimité',
    seatsSubtitle: ({
      used,
      cap,
      pending,
    }: {
      used: number;
      cap: string | number;
      pending: number;
    }) => `${used} sur ${cap} utilisateurs utilisés${pending > 0 ? ` (${pending} invité${pending > 1 ? 's' : ''})` : ''}`,
    inviteMemberCta: 'Inviter un membre',

    roleOwner: 'Propriétaire',
    roleAdmin: 'Admin',
    roleMember: 'Membre',
    roleOptionMember: 'Membre',
    roleOptionAdmin: 'Admin',

    colName: 'Nom',
    colEmail: 'Email',
    colRole: 'Rôle',
    leaveWorkspaceCta: 'Quitter le workspace',
    removeCta: 'Retirer',

    pendingInvitesHeading: 'Invitations en attente',
    noPendingInvites: 'Aucune invitation en attente.',
    inviteColEmail: 'Email',
    inviteColRole: 'Rôle',
    inviteColExpires: 'Expire',
    revokingCta: 'Révocation…',
    revokeCta: 'Révoquer',

    cancelCta: 'Annuler',
    removingCta: 'Retrait…',
    leaveDialogTitle: 'Quitter ce workspace ?',
    removeDialogTitle: ({ name }: { name: string }) => `Retirer ${name} ?`,
    leaveDialogDescription: "Vous perdrez l'accès immédiatement — un propriétaire ou un admin pourra vous réinviter plus tard.",
    removeDialogDescription: ({ name }: { name: string }) => `${name} perd l'accès à ce workspace immédiatement.`,

    emailLabel: 'Email',
    emailPlaceholder: 'teammate@example.com',
    roleLabel: 'Rôle',
    inviteErrorFallback: "Une erreur est survenue lors de l'envoi de cette invitation.",
    sendingInviteCta: 'Envoi…',
    sendInviteCta: "Envoyer l'invitation",

    roleChangeErrorFallback: 'Impossible de changer ce rôle.',
    removeErrorFallback: 'Impossible de retirer ce membre.',
    revokeErrorFallback: 'Impossible de révoquer cette invitation.',
  },
  account: {
    pageTitle: 'Compte',
    loadSignInMethodsError: 'Impossible de charger les méthodes de connexion',
    emailPasswordLabel: 'Email & mot de passe',
    googleLabel: 'Google',

    savingCta: 'Enregistrement…',

    profileHeading: 'Profil',
    displayNameLabel: "Nom d'affichage",
    namePlaceholder: 'Votre nom',
    emailLabel: 'Email',
    saveNameErrorFallback: "Une erreur est survenue lors de l'enregistrement de votre nom.",
    saveCta: 'Enregistrer',
    savedLabel: 'Enregistré',

    passwordHeading: 'Mot de passe',
    passwordMismatchError: 'Le nouveau mot de passe et sa confirmation ne correspondent pas.',
    currentPasswordLabel: 'Mot de passe actuel',
    newPasswordLabel: 'Nouveau mot de passe',
    confirmNewPasswordLabel: 'Confirmez le nouveau mot de passe',
    changePasswordErrorFallback: 'Une erreur est survenue lors du changement de votre mot de passe.',
    changePasswordCta: 'Changer le mot de passe',
    passwordChangedLabel: 'Mot de passe changé',
  },
  adminImpersonate: {
    impersonateFailedFallback: "Impossible d'usurper l'identité de cet utilisateur.",
    lookupFailedFallback: 'Impossible de rechercher cet utilisateur.',
    userNotFound: 'Compte utilisateur introuvable.',
    noOwnerToView: "Ce workspace n'a pas de propriétaire à visualiser.",
  },
  admin: {
    pagination: {
      pageOfTotal: ({
        page,
        totalPages,
        totalCount,
      }: {
        page: number;
        totalPages: number;
        totalCount: string;
      }) => `Page ${page} sur ${totalPages} · ${totalCount} au total`,
      prevCta: 'Précédent',
      nextCta: 'Suivant',
    },
    workspaces: {
      crawlSignalRenderedLabel: 'Dépend du crawl en mode rendu',
      crawlSignalSourcesAcrossBots: ({
        sourceCount,
        botCountLabel,
        botCount,
      }: {
        sourceCount: number;
        botCountLabel: string;
        botCount: number;
      }) => `source${sourceCount > 1 ? 's' : ''} sur ${botCountLabel} bot${botCount > 1 ? 's' : ''}`,
      crawlSignalRenderedFootnote:
        "Quelle part du parc dépend de Cloudflare Browser Rendering — une capacité que nous avons sur le plan Workers Free aujourd'hui, pas Paid.",
      crawlSignalCapacityLabel: 'A atteint notre propre limite de capacité de rendu (7j)',
      crawlSignalSourceCountLabel: ({ n }: { n: number }) => `source${n > 1 ? 's' : ''}`,
      crawlSignalCapacityFootnote:
        "Sources distinctes, notre propre capacité uniquement — jamais le plafond de plan d'un client. Un processus tué ou une tâche périmée nettoyée ne laisse aucune trace — ceci est un plancher. Une hausse continue signifie qu'il est temps de passer à Workers Paid (5 $/mois).",

      emptyText: 'Aucun workspace pour le moment.',
      internalBadge: 'INTERNE',
      cancelsAtPeriodEndHint: 'annulation à la fin de la période',
      colName: 'Nom',
      colOwner: 'Propriétaire',
      colPlan: 'Plan',
      colBots: 'Bots',
      colMembers: 'Membres',
      colMsgsPerMo: 'Msg / mois',
      colPages: 'Pages',
      colCreated: 'Créé',
      colActions: 'Actions',
      noOwnerTitle: "Ce workspace n'a pas de propriétaire",
      viewAsCta: 'Voir en tant que',
      switchingCta: 'Changement…',
      viewAsErrorFallback: 'Échec',

      backToWorkspaces: '← Retour aux Workspaces',
      ownerLabel: 'Propriétaire : ',
      bannedBadge: 'banni',
      createdLabel: ({ date }: { date: string }) => `Créé le ${date}`,
      usageHeading: 'Usage',
      messagesThisMonthLabel: 'Messages ce mois-ci',
      pagesIndexedLabel: 'Pages indexées',
      botsLabel: 'Bots',
      internalUsageNote: "Workspace interne — aucun plafond ne s'applique ; l'usage est toujours enregistré ci-dessus.",
      botsHeading: 'Bots',
      noBotsYet: 'Aucun bot pour le moment.',
      colBotName: 'Nom',
      colPublicId: 'ID public',
      colStatus: 'Statut',
      colBotCreated: 'Créé',
      membersHeading: 'Membres',
      noMembersYet: 'Aucun membre.',
      colEmail: 'Email',
      colMemberName: 'Nom',
      colRole: 'Rôle',
      colJoined: 'Rejoint',
      memberRoleLabel: {
        owner: 'Propriétaire',
        admin: 'Admin',
        member: 'Membre',
      },
      subscriptionHeading: 'Abonnement',
      statusLabel: 'Statut : ',
      periodEndLabel: 'Fin de période : ',
      cancelsAtPeriodEndLabel: 'Annulation à la fin de la période : ',
      yesLabel: 'Oui',
      noLabel: 'Non',
      polarSubscriptionIdLabel: "ID d'abonnement Polar : ",
      manageInPolarLink: 'Gérer dans Polar ↗',
      noSubscriptionText: 'Aucun abonnement — ce workspace est sur le plan gratuit.',
      dangerZoneHeading: 'Zone de danger',
      internalToggleLabel: 'Workspace interne',
      internalToggleHint: "Aucun plafond, aucune facturation — l'usage est toujours enregistré mais jamais transmis à Polar.",
      banOwnerLabel: 'Bannir le propriétaire',
      banOwnerHint: "Les utilisateurs bannis ne peuvent pas se connecter. Le widget et l'API publique ne sont pas affectés.",
      unbanCta: 'Débannir',
      unbanningCta: 'Débannissement…',
      banCta: 'Bannir',
      demoteBeforeBanTitle: 'Rétrogradez cet admin en utilisateur avant de le bannir.',
      roleLabel: 'Rôle',
      roleHint: "Les admins peuvent accéder à tous les écrans d'administration internes.",
      cannotChangeOwnRoleTitle: 'Vous ne pouvez pas changer votre propre rôle.',
      cancelCta: 'Annuler',
      savingCta: 'Enregistrement…',
      confirmCta: 'Confirmer',
      markInternalDialogTitle: 'Marquer ce workspace comme interne ?',
      removeInternalDialogTitle: 'Retirer le statut interne ?',
      markInternalDialogDescription:
        "Ce workspace contournera chaque plafond de plan (bots, messages, pages) et ne sera jamais facturé. L'usage est toujours enregistré pour information, mais la synchronisation nocturne de l'usage vers Polar l'ignore — il n'y a pas d'identité de facturation à laquelle l'envoyer.",
      removeInternalDialogDescription:
        "Ce workspace revient à l'application normale du plan et de la facturation (s'il a un abonnement actif).",
      banDialogTitle: ({ email }: { email: string | undefined }) => `Bannir ${email} ?`,
      banDialogDescription: "Ceci le déconnecte immédiatement et empêche toute future connexion jusqu'à ce qu'il soit débanni.",
      banReasonLabel: 'Raison (facultatif)',
      banReasonPlaceholder: 'Pourquoi ce compte est-il banni ?',
      banningCta: 'Bannissement…',
      banUserCta: "Bannir l'utilisateur",
      roleDialogTitle: ({ role }: { role: 'user' | 'admin' | null }) => `Changer le rôle en ${role} ?`,
      roleDialogDescriptionAdmin: ({ email }: { email: string | undefined }) =>
        `${email} obtiendra l'accès à tous les écrans d'administration internes.`,
      roleDialogDescriptionUser: ({ email }: { email: string | undefined }) =>
        `${email} perdra l'accès aux écrans d'administration internes.`,
      updateWorkspaceErrorFallback: 'Impossible de mettre à jour ce workspace.',
      banUserErrorFallback: 'Impossible de bannir cet utilisateur.',
      unbanUserErrorFallback: 'Impossible de débannir cet utilisateur.',
      changeRoleErrorFallback: 'Impossible de changer le rôle de cet utilisateur.',
    },
    bots: {
      colName: 'Nom',
      colPublicId: 'ID public',
      colWorkspace: 'Workspace',
      colStatus: 'Statut',
      colSources: 'Sources',
      colLastCrawl: 'Dernier crawl',
      colInstanceId: "ID d'instance",
      noBotsYet: 'Aucun bot pour le moment.',

      backToBots: '← Retour aux Bots',
      workspaceLabel: 'Workspace : ',
      instanceLabel: ({ id }: { id: string }) => `Instance : ${id}`,
      createdLabel: ({ date }: { date: string }) => `Créé le ${date}`,
      openOwnerViewCta: 'Ouvrir la vue du propriétaire',
      openingCta: 'Ouverture…',
      openOwnerViewErrorFallback: "Impossible d'ouvrir la vue du propriétaire.",
      sourcesHeading: 'Sources',
      noSourcesYet: 'Aucune source pour le moment.',
      colUrl: 'URL',
      colType: 'Type',
      colSourceStatus: 'Statut',
      colPages: 'Pages',
      colLastCrawled: 'Dernier crawl',
      colError: 'Erreur',
      recentJobsHeading: 'Tâches récentes',
      noJobsLinked: 'Aucune tâche liée à ce bot pour le moment.',
      colKind: 'Type',
      colJobStatus: 'Statut',
      colAttempts: 'Tentatives',
      colUpdated: 'Mis à jour',
      colPayload: 'Contenu',
      recentJobsFootnote:
        "Les tâches récentes sont associées au mieux via le contenu de chaque tâche — une tâche qui ne porte ni l'ID de ce bot ni l'un de ses IDs de source n'apparaîtra pas ici même si elle a touché ce bot indirectement.",
    },
    jobs: {
      tabLabel: {
        all: 'Toutes',
        running: 'En cours',
        failed: 'Échouées',
      },
      noFailedJobs: 'Aucune tâche échouée — la file est saine.',
      noJobsToShow: 'Aucune tâche à afficher.',
      colKind: 'Type',
      colStatus: 'Statut',
      colPayload: 'Contenu',
      colAttempts: 'Tentatives',
      colCreated: 'Créé',
      colUpdated: 'Mis à jour',
      colActions: 'Actions',
      retryCta: 'Réessayer',
      retryingCta: 'Nouvelle tentative…',
      cancelCta: 'Annuler',
      cancellingCta: 'Annulation…',
      cancelJobCta: 'Annuler la tâche',
      keepJobCta: 'Garder la tâche',
      cancelDialogTitle: 'Annuler cette tâche ?',
      retryJobErrorFallback: 'Impossible de réessayer cette tâche.',
      cancelJobErrorFallback: "Impossible d'annuler cette tâche.",
      cancelDialogDescriptionListRow:
        "Ceci supprime la ligne de tâche en attente — elle ne s'exécutera jamais. Seules les tâches en attente peuvent être annulées ainsi ; le gestionnaire d'une tâche en cours peut déjà être en train de la traiter.",

      backToJobs: '← Retour aux Tâches',
      jobIdLabel: 'ID de tâche',
      attemptsLabel: 'Tentatives',
      createdLabel: 'Créé',
      updatedLabel: 'Mis à jour',
      runAfterLabel: 'Exécuter après',
      errorHeading: 'Erreur',
      payloadHeading: 'Contenu',
      cancelDialogDescriptionDetailPage: "Ceci supprime la ligne de tâche en attente — elle ne s'exécutera jamais.",
    },
    posts: {
      description:
        "Articles de blog, brouillons et publiés. Les modifications ici sont en ligne sur embent.com en moins d'une minute après publication.",
      newPostCta: 'Nouvel article',
      emptyText: 'Aucun article pour le moment — créez le premier.',
      colTitle: 'Titre',
      colStatus: 'Statut',
      colAuthor: 'Auteur',
      colPublished: 'Publié',
      colUpdated: 'Mis à jour',
      scheduledLabel: ({ date }: { date: string }) => `programmé · ${date}`,
    },
    postEditor: {
      backToPosts: '← Articles',
      newPostTitle: 'Nouvel article',
      editPostTitleFallback: "Modifier l'article",

      titleLabel: 'Titre',
      titlePlaceholder: 'Comment Embent répond aux questions de support',
      slugLabel: 'Slug',
      slugPlaceholder: 'how-embent-answers-support-questions',
      slugUrlPrefix: 'embent.com/blog/',
      excerptLabel: 'Extrait',
      excerptPlaceholder: 'Une ou deux phrases — affichées sur /blog et utilisées comme meta description.',
      tagsLabel: 'Tags',
      tagsPlaceholder: 'rag, support, launch',
      tagsHint: 'Séparés par des virgules.',
      coverUrlLabel: "URL de l'image de couverture",
      coverUrlPlaceholder: 'https://cdn.embent.com/blog/…',

      authorLabel: 'Auteur',
      editAuthorCta: "Modifier l'auteur",
      newAuthorCta: '+ Nouvel auteur',
      authorSelectPlaceholder: 'Choisissez un auteur',
      noAuthorsYet: 'Aucun auteur pour le moment — ajoutez-en un avec « + Nouvel auteur » ci-dessus.',

      visibilityScheduledText: ({ date }: { date: string }) => `En ligne le ${date}`,
      visibilityLiveText: ({ date }: { date: string }) => `Publié le ${date}`,
      visibilityDraftText: 'Pas encore publié',
      publishDateLabel: 'Date de publication',
      publishDateHintPrefix: 'Les horaires sont dans ',
      publishDateHintSuffix:
        " — l'horloge de ce navigateur. Laissez vide pour publier au moment même où vous cliquez sur publier. Définissez une heure future et l'article reste caché jusque-là — il disparaît du blog, de ses compteurs de tags, du sitemap et du flux RSS jusqu'à ce que l'horloge le rattrape. Rien n'a besoin de tourner pour qu'il apparaisse.",

      bodyLabel: 'Corps (Markdown)',
      livePreviewLabel: 'Aperçu en direct',

      missingFieldLabel: {
        title: 'titre',
        slug: 'slug',
        excerpt: 'extrait',
        body: 'corps',
        author: 'auteur',
      },
      beforeSavingAdd: ({ fields }: { fields: string }) => `Avant d'enregistrer, ajoutez : ${fields}.`,

      savingCta: 'Enregistrement…',
      saveChangesCta: 'Enregistrer les modifications',
      saveDraftCta: 'Enregistrer le brouillon',
      unpublishingCta: 'Dépublication…',
      schedulingCta: 'Programmation…',
      publishingCta: 'Publication…',
      unpublishCta: 'Dépublier',
      scheduleCta: 'Programmer',
      publishCta: 'Publier',
      savedFlash: 'Enregistré',

      renderingPreview: 'Rendu…',
      previewFailedPrefix: "Échec du chargement de l'aperçu : ",

      uploadFailedFallback: 'Échec du téléversement',
      bodyPlaceholder: 'Écrivez en Markdown… collez ou glissez une image pour la téléverser en ligne.',
      uploadingText: 'Téléversement…',
      imageUploadFailedPrefix: "Échec du téléversement de l'image : ",

      deleteCta: 'Supprimer',
      deleteDialogTitle: ({ title }: { title: string }) => `Supprimer « ${title} » ?`,
      deleteDialogDescriptionPrefix: 'Supprime définitivement cet article. Cette action est irréversible. Tapez',
      deleteDialogDescriptionSuffix: ' pour confirmer.',
      confirmPostTitleAriaLabel: "Confirmer le titre de l'article",
      cancelCta: 'Annuler',
      deletingCta: 'Suppression…',
      deletePostCta: "Supprimer l'article",

      editAuthorDialogTitle: ({ name }: { name: string }) => `Modifier ${name}`,
      newAuthorDialogTitle: 'Nouvel auteur',
      authorDialogDescription: 'Uniquement de vraies signatures — aucune identité fabriquée.',
      authorNameLabel: 'Nom',
      authorNamePlaceholder: 'Johnny Bui',
      authorRoleLabel: 'Rôle',
      authorRolePlaceholder: 'Fondateur',
      authorAvatarLabel: "URL de l'avatar",
      authorAvatarPlaceholder: 'https://cdn.embent.com/…',
      authorBioLabel: 'Bio',
      authorPersonaLabel: 'Persona (interne)',
      authorPersonaPlaceholder:
        'Écrit des phrases courtes et directes. Sceptique face au battage médiatique. Préfère un exemple concret à un adjectif.',
      authorPersonaHint:
        "Une note de cadrage pour rédiger dans le ton de cette signature — jamais publiée, jamais montrée aux lecteurs.",
      authorSavingCta: 'Enregistrement…',
      authorAddingCta: 'Ajout…',
      saveAuthorCta: "Enregistrer l'auteur",
      addAuthorCta: 'Ajouter un auteur',

      writingGuideHeading: 'Guide de rédaction',
      writingGuideSubtitle: 'Support Markdown, images, et un prompt de cadrage IA',
      supportedMarkdownHeading: 'Markdown pris en charge',
      mdListHeadings: 'Titres, niveaux 1 à 6',
      mdListLists: 'Listes à puces et numérotées',
      mdListLinksPrefix: 'Liens — ',
      mdListLinksSuffix: ', http(s) et mailto uniquement',
      mdListImages:
        "Images n'importe où dans le corps, y compris en plein milieu de l'article — collez ou glissez-en une (voir ci-dessous)",
      mdListCode: 'Blocs de code en ligne et en bloc, avec coloration syntaxique',
      mdListTables: 'Tableaux, façon GitHub, avec alignement des colonnes',
      mdListBlockquotes: 'Citations',
      strippedHeading: 'Ce qui est retiré',
      strippedPrefix: 'Tout le reste. Les balises HTML brutes, et ',
      strippedSuffix:
        " le contenu spécifiquement, ne survivent jamais — le corps est nettoyé selon une liste fixe d'éléments autorisés (titres, listes, liens, images, code, tableaux, citations, plus les paragraphes/emphases qui les composent) avant d'être affiché n'importe où, y compris ici dans l'aperçu.",
      imagesHeading: 'Images',
      imagesPrefix: 'Collez ou glissez une image dans le corps — elle est téléversée sur cdn.embent.com et insérée',
      imagesSuffix:
        " au niveau du curseur, où qu'il se trouve, pour qu'elle puisse atterrir en plein milieu d'un paragraphe. L'image de couverture ci-dessus est un champ séparé — elle ne provient jamais de quelque chose de déposé dans le corps.",
      copiedCta: 'Copié !',
      copyAiPromptCta: 'Copier le prompt IA',
      copiedToClipboard: 'Copié dans le presse-papiers.',
      clipboardUnavailable: "Le presse-papiers n'est pas disponible dans ce navigateur — sélectionnez et copiez le prompt ci-dessous.",
      assemblesPrefix: 'Assemble le style maison, ',
      assemblesAuthorPersonaClause: ({ authorName }: { authorName: string }) => `le persona de ${authorName}`,
      assemblesNoAuthorClause: "une note indiquant qu'aucun auteur n'est sélectionné",
      assemblesSuffix:
        ", les conventions Markdown ci-dessus, et une structure demandée — collez-le dans un modèle externe, puis recollez le brouillon dans le corps.",

      aiPromptPersonaWithVoice: ({ authorName, persona }: { authorName: string; persona: string }) =>
        `Écrivez dans le style de ${authorName} :\n${persona}`,
      aiPromptPersonaNoPersona: ({ authorName }: { authorName: string }) =>
        `${authorName} n'a pas de persona défini — écrivez dans un style neutre et direct.`,
      aiPromptAuthorFallbackLower: 'cet auteur',
      aiPromptAuthorFallbackUpper: 'Cet auteur',
      aiPromptTemplate: ({ personaSection }: { personaSection: string }) =>
        `Vous rédigez un article de blog pour Embent (embent.com), une plateforme d'agents IA pour sites web.

Style maison :
- Direct et concret. Pas de battage, pas de discours marketing.
- Paragraphes courts (2 à 4 phrases).
- N'inventez jamais de statistiques ou de noms de clients. Si vous n'avez pas un vrai chiffre ou un vrai client, n'en inventez pas.

Ton de l'auteur :
${personaSection}

Conventions Markdown (c'est TOUT ce qui sera affiché — tout le reste est retiré) :
- Titres (#, ##, ###), listes à puces et numérotées, citations.
- Liens en [texte](url) — http(s) et mailto uniquement.
- Images n'importe où dans le corps, y compris en plein milieu de l'article, en ![alt](url).
- Blocs de code en ligne et en bloc.
- Tableaux (façon GitHub).
- Le HTML brut et les balises <script>/<style> sont entièrement retirés — écrivez uniquement en Markdown, pas de HTML.

Structure demandée :
1. Titre
2. Accroche en un paragraphe
3. 4 à 7 sections, chacune avec un titre h2 (##)
4. Une courte conclusion

Rédigez le brouillon complet maintenant, en Markdown.`,
    },
    feedback: {
      filterLabel: {
        unhandled: 'Non traité',
        all: 'Tous',
      },
      typeLabel: {
        bug: 'Bug',
        feature: 'Fonctionnalité',
        question: 'Question',
      },
      mailtoSubject: ({ id, type }: { id: string; type: string }) => `Re : [Retour Embent ${id}] ${type}`,
      replyCta: 'Répondre',
      colDate: 'Date',
      colWorkspace: 'Workspace',
      colUser: 'Utilisateur',
      colType: 'Type',
      colMessage: 'Message',
      colActions: 'Actions',
      emptyUnhandled: 'Aucun retour non traité — boîte vide.',
      emptyAll: 'Aucun retour pour le moment.',
      updateFeedbackErrorFallback: 'Impossible de mettre à jour ce retour.',
      reopeningCta: 'Réouverture…',
      markingCta: 'Marquage…',
      reopenCta: 'Rouvrir',
      markHandledCta: 'Marquer comme traité',
      noBotFallback: 'aucun bot',
      noUaFallback: 'aucun UA',
    },
    playground: {
      demosAllTimeLabel: 'Démos au total',
      demos30dLabel: 'Démos · 30j',
      messagesAllTimeLabel: 'Messages au total',
      signedUpAllTimeLabel: 'Inscrits',
      signedUp30dLabel: 'Inscrits · 30j',
      leadsAllTimeLabel: 'Leads capturés',
      leads30dLabel: 'Leads · 30j',
      demosPerDayLabel: 'Démos par jour',
      messagesPerDayLabel: 'Messages par jour',
      demoUnit: ({ count }: { count: number }): string => (count > 1 ? 'démos' : 'démo'),
      messageUnit: ({ count }: { count: number }): string => (count > 1 ? 'messages' : 'message'),
      noDemos30d: 'Aucune démo ces 30 derniers jours.',
      statusBreakdownHeading: 'Répartition par statut (30j)',
      recentDomainsHeading: 'Domaines récents',
      noDemosYet: 'Aucune démo pour le moment.',
      colHostname: 'Domaine',
      colStatus: 'Statut',
      colPages: 'Pages',
      colMessages: 'Messages',
      colAttempts: 'Tentatives',
      colSignedUp: 'Inscrit',
      colLeads: 'Leads',
      colCreated: 'Créé',
      signedUpAriaLabel: 'inscrit',
      notSignedUpAriaLabel: 'non inscrit',
      noEmailFallback: "(pas d'email)",
    },
  },
} satisfies Catalog;
